/**
 * SalesManagerPieChart Component
 *
 * Pie/Donut chart showing total gross distribution among sales managers.
 * Uses blue palette to match NEW_VEHICLE department theme.
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { SalesManager } from './types';

interface SalesManagerPieChartProps {
  managers: SalesManager[];
  totalGross: number;
}

/**
 * Color palette for pie slices
 * Using blue-based colors to match NEW_VEHICLE department theme
 */
const COLORS = [
  '#2563eb', // blue-600
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#f97316', // orange-500
  '#fb923c', // orange-400
  '#fdba74', // orange-300
  '#10b981', // emerald-500
  '#34d399', // emerald-400
  '#6ee7b7', // emerald-300
  '#a78bfa', // violet-400
  '#c4b5fd', // violet-300
];

/**
 * Format currency for display
 */
function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

/**
 * Custom tooltip component
 */
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1 truncate">{data.name}</p>
        <p className="text-sm text-gray-600">
          Total Gross:{' '}
          <span className="font-medium text-blue-600">
            {formatCurrency(data.value)}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          Share: <span className="font-medium">{data.percentage.toFixed(1)}%</span>
        </p>
        <p className="text-sm text-gray-600">
          Units: <span className="font-medium">{data.units}</span>
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Custom legend formatter
 */
const renderLegendText = (value: string) => {
  const maxLength = 15;
  if (value.length > maxLength) {
    return value.substring(0, maxLength) + '...';
  }
  return value;
};

export const SalesManagerPieChart: React.FC<SalesManagerPieChartProps> = ({
  managers,
  totalGross,
}) => {
  const chartData = useMemo(() => {
    const MAX_SLICES = 8;

    if (managers.length === 0 || totalGross === 0) {
      return [];
    }

    // Sort by total gross descending
    const sorted = [...managers].sort(
      (a, b) => b.totalGross - a.totalGross
    );

    if (sorted.length <= MAX_SLICES) {
      return sorted.map((manager, index) => ({
        name: manager.managerName,
        value: manager.totalGross,
        percentage: (manager.totalGross / totalGross) * 100,
        units: manager.totalUnits,
        color: COLORS[index % COLORS.length],
      }));
    }

    // Group smaller managers into "Others"
    const top = sorted.slice(0, MAX_SLICES - 1);
    const others = sorted.slice(MAX_SLICES - 1);
    const othersTotal = others.reduce((sum, m) => sum + m.totalGross, 0);
    const othersUnits = others.reduce((sum, m) => sum + m.totalUnits, 0);

    const result = top.map((manager, index) => ({
      name: manager.managerName,
      value: manager.totalGross,
      percentage: (manager.totalGross / totalGross) * 100,
      units: manager.totalUnits,
      color: COLORS[index % COLORS.length],
    }));

    if (othersTotal > 0) {
      result.push({
        name: `Others (${others.length})`,
        value: othersTotal,
        percentage: (othersTotal / totalGross) * 100,
        units: othersUnits,
        color: '#9ca3af', // gray-400
      });
    }

    return result;
  }, [managers, totalGross]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
        No data to display
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={75}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            formatter={renderLegendText}
            wrapperStyle={{ fontSize: '12px', paddingLeft: '10px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
